import { useState } from 'react';
import { 
  FaLightbulb, 
  FaGlobe, 
  FaTimes, 
  FaCheck, 
  FaArrowRight, 
  FaUsers, 
  FaCalendar, 
  FaLink,
  FaCode,
  FaExternalLinkAlt,
  FaEye
} from 'react-icons/fa';
import styles from './Portfolio.module.css'; 

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [previewMode, setPreviewMode] = useState('info'); // 'info' or 'preview'

  const projects = [
    {
      id: 1,
      title: "Libélula Hotel",
      summary: "Plataforma web moderna para hotel en Puerto Maldonado",
      image: "/projects/libelula.png",
      category: "Web Development",
      client: "Libélula Hotel",
      date: "2025",
      description: "Desarrollo de una plataforma web moderna y funcional para Libélula Hotel, ubicado en Puerto Maldonado, que combina comodidad, innovación y un impacto positivo en la comunidad.",
      features: [
        "Sistema de reservas en línea",
        "Gestión de habitaciones",
        "Integración con redes sociales",
        "Panel de administración personalizado",
        "Diseño responsive",
        "Optimización SEO"
      ],
      technologies: ["Angular", "Node.js", "PocketBase", "AWS"],
      results: [
        "Mejora en la visibilidad online del hotel",
        "Incremento en reservas directas",
        "Optimización de procesos internos",
        "Mejor experiencia de usuario"
      ],
      liveUrl: "https://libelulahotel.com.pe/",
      testimonial: {
        text: "Una solución que transformó nuestra presencia digital y mejoró la experiencia de nuestros huéspedes.",
        author: "Gerencia",
        position: "Libélula Hotel"
      }
    },
    {
      id: 1,
      title: "Red Tesla Net",
      summary: "Proveedor líder de internet de alta velocidad",
      image: "/projects/redtesla.png",
      category: "Telecomunicaciones",
      client: "Red Tesla Net",
      date: "2025",
      description: "Implementación de plataforma integral para proveedor de internet de fibra óptica con cobertura nacional",
      features: [
        "Sistema de gestión de cobertura",
        "Mapa interactivo de zonas de servicio",
        "Panel de control para clientes",
        "Sistema de soporte técnico integrado",
        "Monitoreo de red en tiempo real",
        "Plataforma de autogestión"
      ],
      technologies: ["Angular", "Cloudflare", "Leaflet", "Node.js", "AWS"],
      results: [
        "Cobertura incrementada en 40%",
        "Reducciόn de tickets de soporte en 35%",
        "Mejora en tiempo de respuesta de red",
        "Aumento de satisfacción del cliente"
      ],
      liveUrl: "https://redteslanet.com/",
      testimonial: {
        text: "Una solución tecnológica que revolucionó nuestra capacidad operativa y atención al cliente.",
        author: "Director Técnico",
        position: "Red Tesla Net"
      }
    }
  ];

  return (
    <>
      <section className={styles.portfolioHero}>
        <div className={styles.heroContent}>
          <div className={styles.badge}>
            <FaLightbulb className={styles.badgeIcon} />
            <span>Nuestro Portafolio</span>
          </div>
          <h1>Proyectos que <span>Transforman</span> Ideas</h1>
          <p>
            Descubre cómo ayudamos a nuestros clientes a alcanzar sus objetivos digitales 
            a través de soluciones tecnológicas innovadoras y personalizadas.
          </p>
        </div>
      </section>

      <section className={styles.portfolioSection}>
        <div className={styles.portfolioContainer}>
          <div className={styles.projectsGrid}>
            {projects.map((project) => (
              <div key={project.id} className={styles.projectCard} onClick={() => setSelectedProject(project)}>
                <div className={styles.projectImage}>
                  <div className={styles.imageContainer}>
                    <img src={project.image} alt={project.title} className={styles.projectImg} />
                    <div className={styles.previewContainer}>
                      <iframe 
                        src={project.liveUrl} 
                        title={`${project.title} Preview`}
                        className={styles.previewIframe}
                      />
                    </div>
                  </div>
                  <div className={styles.projectOverlay}>
                    <div className={styles.overlayContent}>
                      <FaEye className={styles.overlayIcon} />
                      <span>Ver Proyecto</span>
                    </div>
                  </div>
                </div>
                <div className={styles.projectInfo}>
                  <span className={styles.projectCategory}>{project.category}</span>
                  <h3>{project.title}</h3>
                  <p>{project.summary}</p>
                  <div className={styles.projectActions}>
                    <button className={styles.learnMore}>
                      Ver Detalles <FaArrowRight />
                    </button>
                    <a 
                      href={project.liveUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.visitLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaExternalLinkAlt />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedProject && (
        <div className={styles.modalOverlay} onClick={() => setSelectedProject(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={() => setSelectedProject(null)}>
              <FaTimes />
            </button>
            
            <div className={styles.modalHeader}>
              <div className={styles.modalTabs}>
                <button 
                  className={`${styles.tabButton} ${previewMode === 'info' ? styles.active : ''}`}
                  onClick={() => setPreviewMode('info')}
                >
                  <FaCode /> Información
                </button>
                <button 
                  className={`${styles.tabButton} ${previewMode === 'preview' ? styles.active : ''}`}
                  onClick={() => setPreviewMode('preview')}
                >
                  <FaGlobe /> Vista Previa
                </button>
              </div>
            </div>
            
            <div className={styles.modalScroll}>
              {previewMode === 'preview' ? (
                <div className={styles.previewSection}>
                  <div className={styles.previewHeader}>
                    <h2>{selectedProject.title}</h2>
                    <a 
                      href={selectedProject.liveUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.openInNewTab}
                    >
                      <FaExternalLinkAlt /> Abrir en nueva pestaña
                    </a>
                  </div>
                  <div className={styles.iframeContainer}>
                    <iframe 
                      src={selectedProject.liveUrl} 
                      title={`${selectedProject.title} Live Preview`}
                      className={styles.livePreviewIframe}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.modalContent}>
                  <div className={styles.projectHeader}>
                    <div className={styles.imageWrapper}>
                      <img 
                        src={selectedProject.image} 
                        alt={selectedProject.title} 
                        className={styles.modalImage}
                      />
                      <div className={styles.imageOverlay}>
                        <FaGlobe className={styles.overlayIcon} />
                      </div>
                    </div>
                    <div className={styles.headerContent}>
                      <h2>{selectedProject.title}</h2>
                      <p>{selectedProject.description}</p>
                    </div>
                  </div>

                  <div className={styles.modalGrid}>
                    <div className={`${styles.modalSection} ${styles.featuresSection}`}>
                      <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>
                          <FaCheck />
                        </div>
                        <h3>Características</h3>
                      </div>
                      <ul className={styles.featuresList}>
                        {selectedProject.features.map((feature, index) => (
                          <li key={index} className={styles.featureItem}>
                            <FaCheck className={styles.checkIcon} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={`${styles.modalSection} ${styles.techSection}`}>
                      <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>
                          <FaCode />
                        </div>
                        <h3>Tecnologías</h3>
                      </div>
                      <div className={styles.techGrid}>
                        {selectedProject.technologies.map((tech, index) => (
                          <span key={index} className={styles.techBadge}>
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className={`${styles.modalSection} ${styles.resultsSection}`}>
                      <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>
                          <FaLightbulb />
                        </div>
                        <h3>Resultados</h3>
                      </div>
                      <div className={styles.resultsList}>
                        {selectedProject.results.map((result, index) => (
                          <div key={index} className={styles.resultItem}>
                            <FaCheck className={styles.checkIcon} />
                            <span>{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className={styles.projectMeta}>
                    <div className={styles.metaItem}>
                      <FaUsers />
                      <div>
                        <h4>Cliente</h4>
                        <p>{selectedProject.client}</p>
                      </div>
                    </div>
                    <div className={styles.metaItem}>
                      <FaCalendar />
                      <div>
                        <h4>Año</h4>
                        <p>{selectedProject.date}</p>
                      </div>
                    </div>
                    <div className={styles.metaItem}>
                      <FaGlobe />
                      <div>
                        <h4>Website</h4>
                        <a href={selectedProject.liveUrl} target="_blank" rel="noopener noreferrer">
                          Ver sitio <FaLink />
                        </a>
                      </div>
                    </div>
                  </div>

                  {selectedProject.testimonial && (
                    <div className={styles.testimonial}>
                      <p>"{selectedProject.testimonial.text}"</p>
                      <div className={styles.testimonialAuthor}>
                        <strong>{selectedProject.testimonial.author}</strong>
                        <span>{selectedProject.testimonial.position}</span>
                      </div>
                    </div>
                  )}

                  <div className={styles.modalFooter}>
                    <a 
                      href={selectedProject.liveUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className={styles.visitButton}
                    >
                      Visitar Proyecto <FaArrowRight />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Portfolio;